import { Link, type LinkProps } from 'react-router'
import { useTranslation } from 'react-i18next'
import { localizedRoutes, type RouteKey } from '@/i18n/routes'
import type { SupportedLanguage } from '@/i18n'

interface LocalizedLinkProps extends Omit<LinkProps, 'to'> {
  to: RouteKey | string
}

export function LocalizedLink({ to, children, onClick, ...props }: LocalizedLinkProps) {
  const { i18n } = useTranslation()
  const currentLang = i18n.language as SupportedLanguage

  const [routeOrPath, hash = ''] = String(to).split('#')

  let path: string

  if (routeOrPath in localizedRoutes) {
    const routeKey = routeOrPath as RouteKey
    const localizedPath = localizedRoutes[routeKey][currentLang]
    path = localizedPath ? `/${currentLang}/${localizedPath}` : `/${currentLang}`
  } else if (routeOrPath.startsWith('/')) {
    // Absolute path - add language prefix
    path = `/${currentLang}${routeOrPath}`
  } else if (routeOrPath.startsWith('http') || routeOrPath.startsWith('mailto:') || routeOrPath.startsWith('tel:')) {
    // External link - pass through
    path = routeOrPath
  } else {
    // Relative path - add language prefix
    path = `/${currentLang}/${routeOrPath}`
  }

  if (hash) {
    path = `${path}#${hash}`
  }

  const handleClick = (event: React.MouseEvent<HTMLAnchorElement>) => {
    if (!hash && (!props.target || props.target === '_self') && !event.ctrlKey && !event.metaKey && !event.shiftKey && !event.altKey) {
      // Senza behavior: decide scroll-behavior di html (morbido, ma non con «riduci movimento»).
      window.scrollTo({ top: 0 })
    }
    onClick?.(event)
  }

  return (
    <Link to={path} onClick={handleClick} {...props}>
      {children}
    </Link>
  )
}
